import TeamingTiptapCommitment from "@teaming/tiptap-commitment";
import TeamingCreatedOnAttribute from "@teaming/tiptap-created-on-attribute";
import TeamingCreatorAttribute from "@teaming/tiptap-creator-attribute";
import TeamingTiptapDecision from "@teaming/tiptap-decision";
import TiptapSlashCommands from "@teaming/tiptap-slash-commands";
import TeamingTiptapTopic from "@teaming/tiptap-topic";
import UuidExtension from "@teaming/tiptap-uuid-attribute";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { Editor } from "@tiptap/vue-3";
import EventEmitter from "eventemitter3";
import { v4 as uuidv4 } from "uuid";
const debugFactory = require( "debug" );

import { getProvider, streamNoteLink as noteLink, StreamYdoc } from "@teaming/common-team";

const debug = debugFactory( "teaming:slice-engine:documentManager" );

const noop = () => {};

const staticExtensions = [
	StarterKit.configure( { history: false, } ),
	Image.configure( {
		HTMLAttributes: {
			class: "teaming-text-editor__image",
		},
		inline: true,
	} ),
	Link,
	Underline,
	TiptapSlashCommands,
	TeamingCreatedOnAttribute,
	UuidExtension,
];

/** {documentName:HocusPocusProvider} */
const providerMap = {};

/** {documentName:YStreamDocument} */
const streamDocMap = {};

/** {documentName:API} */
const managerMap = {};

/** {subDocId:Editor} */
const editorMap = {};

const methods = {
	isConnected( documentName ) {
		return !!providerMap[ documentName ];
	},

	connect( documentName ) {
		debug( `connecting to ${ documentName }` );
		if ( providerMap[ documentName ] ) {
			// eslint-disable-next-line no-console
			console.warn( `attempted to connect to ${ documentName } but the document is already managed` );
			return;
		}

		const subDocsAvailable = () => {
			this.emit( `${ documentName }.subDocs` );
		};

		const { auth, i18n, } = this;
		const streamDocument = new StreamYdoc( { documentName, } );
		streamDocument.ydoc.on( "update", subDocsAvailable );
		const hocusPocus = getProvider( {
			documentName,
			ydoc: streamDocument.ydoc,
			token: () => auth.getIdToken(),
			onSynced: () => {
				this.emit( `${ documentName }.connected`, documentName );
				subDocsAvailable();
			},
			translate: i18n.global.t,
		} );

		streamDocMap[ documentName ] = streamDocument;
		providerMap[ documentName ] = hocusPocus;
	},

	// eslint-disable-next-line max-statements
	disconnect( documentName, removeListeners = true ) {
		debug( `disconnecting from ${ documentName }` );
		const provider = providerMap[ documentName ];

		if ( !provider ) {
			// eslint-disable-next-line no-console
			console.warn( `attempted to disconnect from ${ documentName }, but the provider was not found` );
			return;
		}

		provider.disconnect();
		provider.destroy();
		providerMap[ documentName ] = null;
		delete providerMap[ documentName ];

		const streamDocument = streamDocMap[ documentName ];

		if ( streamDocument ) {
			const subDocs = streamDocument.getSubDocs();
			const subDocIds = subDocs.map( subDoc => subDoc.subDocId );
			for ( const subDocId of subDocIds ) {
				const editor = editorMap[ subDocId ];
				if ( editor ) {
					editor.destroy();
				}
				editorMap[ subDocId ] = null;
				delete editorMap[ subDocId ];
			}
			streamDocument.purge(); // unsubscribes ydoc events
			streamDocMap[ documentName ] = null;
			delete streamDocMap[ documentName ];
		}

		managerMap[ documentName ] = null;
		delete managerMap[ documentName ];

		this.emit( `${ documentName }.disconnected`, documentName );
		if ( removeListeners ) {
			this.removeAllListeners( `${ documentName }.connected` );
			this.removeAllListeners( `${ documentName }.subDocs` );
			this.removeAllListeners( `${ documentName }.disconnected` );
			this.removeAllListeners( `${ documentName }.editorActivated` );
			this.removeAllListeners( `${ documentName }.editorDeactivated` );
			this.removeAllListeners( `${ documentName }.editorFocused` );
			this.removeAllListeners( `${ documentName }.editorBlurred` );
		}
	},

	manage( documentName ) {
		if ( !providerMap[ documentName ] ) {
			throw new Error( `the registry is not yet managing ${ documentName }` );
		}

		const manager = managerMap[ documentName ];
		if ( manager ) { return manager; }

		// eslint-disable-next-line consistent-this
		const registry = this;

		/** {documentName:subDocId} */
		const activeSubDocMap = {};

		/** {documentName:subDocId} */
		const focusedSubDocMap = {};

		let latestSubDocId = "";

		managerMap[ documentName ] = {
			activateEditor( subDocId ) {
				const previousSubDocId = activeSubDocMap[ documentName ];
				if ( previousSubDocId && previousSubDocId !== subDocId ) {
					this.deactivateEditor( previousSubDocId );
				}
				activeSubDocMap[ documentName ] = subDocId;
				registry.emit( `${ documentName }.editorActivated`, subDocId );
				return this.getActiveEditor();
			},
			addEditor( {
				subDocId,
				placeholder = "",
				autofocus = false,
				currentUser = {},
				events = {},
				callback = noop,
			} ) {
				let editor = editorMap[ subDocId ];
				if ( editor ) {
					return editor;
				}

				const provider = providerMap[ documentName ];
				const streamDocument = streamDocMap[ documentName ];

				editor = new Editor( {
					// content appears to come directly from the ydoc instance
					// so we don't set it on the editor
					...events,
					extensions: [
						...staticExtensions,
						Collaboration.configure( {
							document: streamDocument.ydoc,
							field: subDocId,
						} ),
						CollaborationCursor.configure( {
							provider,
							user: currentUser,
						} ),
						Placeholder.configure( {
							placeholder,
						} ),
						TeamingCreatorAttribute.configure( {
							getPersonId: () => currentUser.personId,
						} ),
						TeamingTiptapTopic.configure( {
							userName: currentUser.name,
							onTeamingExtensionCallback: callback,
						} ),
						TeamingTiptapCommitment.configure( {
							onTeamingExtensionCallback: callback,
						} ),
						TeamingTiptapDecision.configure( {
							onTeamingExtensionCallback: callback,
						} ),
					],
					autofocus,
				} );

				// Force content into the editor; this will be pushed to the ydoc
				// and magically make it "notice" the schema of the fragment. THEN
				// immediately remove that content (blank note) which will preserve
				// the fragment schema, but remove the text content (why, I don't know).
				if ( this.isEmpty( subDocId ) ) {
					editor.commands.setContent( {
						type: "paragraph",
						content: [
							{
								text: "let's write something...",
								type: "text",
							},
						],
					} );
					editor.commands.clearContent( true );
				}

				editor.TEAMING_NAME = subDocId;
				editorMap[ subDocId ] = editor;
				return editor;
			},
			addSubDoc( {
				title,
				creator,
				virtualMeetingUrl = "",
			} ) {
				const streamYDoc = this.getStreamDoc();
				const uuid = uuidv4().replace( /-/g, "_" );
				const subDocId = `T_${ uuid }`;
				const { date, } = streamYDoc.addSubDoc( { subDocId, title, creator, virtualMeetingUrl, } );
				const link = noteLink( subDocId );
				latestSubDocId = subDocId;
				registry.emit( `${ documentName }.subDocAdded`, subDocId );
				return { subDocId, title, date, link, virtualMeetingUrl, };
			},
			blurEditor( subDocId ) {
				const focusedSubDocId = focusedSubDocMap[ documentName ];
				if ( focusedSubDocId && focusedSubDocId === subDocId ) {
					focusedSubDocMap[ documentName ] = null;
					delete focusedSubDocMap[ documentName ];
					registry.emit( `${ documentName }.editorBlurred`, subDocId );
					return this.getEditor( subDocId );
				}
				return null;
			},
			deactivateEditor( subDocId ) {
				const activeSubDocId = activeSubDocMap[ documentName ];
				if ( activeSubDocId === subDocId ) {
					const editor = this.getActiveEditor();
					activeSubDocMap[ documentName ] = null;
					delete activeSubDocMap[ documentName ];
					registry.emit( `${ documentName }.editorDeactivated`, subDocId );
					return editor;
				}
			},
			focusEditor( subDocId ) {
				focusedSubDocMap[ documentName ] = subDocId;
				registry.emit( `${ documentName }.editorFocused`, subDocId );
				return this.getFocusedEditor();
			},
			getActiveEditor() {
				const subDocId = activeSubDocMap[ documentName ] || "";
				return this.getEditor( subDocId );
			},
			getActiveSubDoc() {
				const subDocId = activeSubDocMap[ documentName ] || "";
				return this.getSubDoc( subDocId );
			},
			getEditor( subDocId ) {
				return editorMap[ subDocId ];
			},
			getFocusedEditor() {
				const subDocId = focusedSubDocMap[ documentName ];
				return this.getEditor( subDocId );
			},
			getProvider() {
				return providerMap[ documentName ];
			},
			getStreamDoc() {
				return streamDocMap[ documentName ];
			},
			getSubDoc( subDocId ) {
				const subDocs = this.getSubDocs();
				return subDocs.find( subDoc => subDoc.subDocId === subDocId );
			},
			getSubDocs() {
				const streamDocument = this.getStreamDoc();
				return streamDocument.getSubDocs();
			},
			getLatestSubDoc() {
				return this.getSubDoc( latestSubDocId );
			},
			removeSubDoc( subDocId ) {
				const streamDoc = this.getStreamDoc();
				streamDoc.removeSubDoc( { subDocId, } );
				registry.emit( `${ documentName }.subDocs` );
			},
			changeSubDocDate( subDocId, date ) {
				const streamDoc = this.getStreamDoc();
				streamDoc.changeSubDocDate( { subDocId, date, } );
				registry.emit( `${ documentName }.subDocs` );
			},
			changeSubDocTitle( subDocId, title ) {
				const streamDoc = this.getStreamDoc();
				streamDoc.changeSubDocTitle( { subDocId, title, } );
				registry.emit( `${ documentName }.subDocs` );
			},
			changeVirtualMeetingUrl( subDocId, virtualMeetingUrl ) {
				const streamDoc = this.getStreamDoc();
				streamDoc.changeSubDocVirtualMeetingUrl( { subDocId, virtualMeetingUrl, } );
				registry.emit( `${ documentName }.subDocs` );
			},
			isEmpty( subDocId ) {
				const streamDocument = this.getStreamDoc();
				const serialized = streamDocument.ydoc.getXmlFragment( subDocId ).toString();
				// see: https://stackoverflow.com/a/5002161/223147
				return serialized.replace( /<\/?[^>]+(>|$)/g, "" ).trim() === "";
			},
			/**
			 * @param {String} subDocId
			 * @param {id:String, text:String} topic
			 */
			injectTopic( topic, subDocId = "" ) {
				let editor = this.getActiveEditor();
				if ( subDocId ) {
					editor = this.getEditor( subDocId );
				}

				if ( !editor ) {
					// eslint-disable-next-line no-console
					return console.warn( "attempted to inject a topic into a non-existent editor:", subDocId );
				}

				editor.commands.insertContent( [
					{
						type: "teaming-topic",
						attrs: {
							"suggested-topic-id": topic.id,
						},
						content: [
							{
								type: "text",
								text: topic.text,
							},
						],
					},
				] );
				return editor;
			},
			injectImage( url, title, subDocId = "" ) {
				let editor = this.getActiveEditor();
				if ( subDocId ) {
					editor = this.getEditor( subDocId );
				}

				if ( !editor ) {
					// eslint-disable-next-line no-console
					return console.warn( "attempted to inject an image into a non-existent editor:", subDocId );
				}

				// empty
				if ( url === "" ) {
					return;
				}

				// update link
				editor.chain()
					.setImage( { src: url, alt: title, title, } )
					.run();

				return editor;
			},
			injectLink( url, subDocId = "" ) {
				let editor = this.getActiveEditor();
				if ( subDocId ) {
					editor = this.getEditor( subDocId );
				}

				if ( !editor ) {
					// eslint-disable-next-line no-console
					return console.warn( "attempted to inject a link into a non-existent editor:", subDocId );
				}

				// empty
				if ( url === "" ) {
					editor.chain()
						.extendMarkRange( "link" )
						.unsetLink()
						.run();

					return;
				}

				// update link
				editor.chain()
					.extendMarkRange( "link" )
					.setLink( { href: url, } )
					.run();
			},
			injectCommitment( text, creator = null, subDocId = "" ) {
				let editor = this.getActiveEditor();
				if ( subDocId ) {
					editor = this.getEditor( subDocId );
				}

				if ( !editor ) {
					// eslint-disable-next-line no-console
					return console.warn( "attempted to inject a commitment into a non-existent editor:", subDocId );
				}

				editor.commands.insertContent( {
					type: "teaming-commitment",
					attrs: {
						"created-on": null,
						id: null,
						creator,
						"due-date": null,
						"assigned-to": creator,
						"assigned-by": creator,
						"completed-date": null,
					},
					content: [
						{
							type: "text",
							text,
						},
					],
				} );
			},
		};

		return managerMap[ documentName ];
	},
};

let registry;

export default ( auth, i18n, config ) => {
	if ( registry ) {
		return registry;
	}
	if ( config.createTeamingGlobalVariable ) {
		window.__TEAMING__.editorMap = editorMap;
	}
	registry = Object.create( new EventEmitter() );
	Object.assign( registry, methods, {
		auth,
		i18n,
	} );
	return registry;
};
